import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  WalletIcon,
  QrCodeIcon,
  LockClosedIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  HomeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  ClockIcon,
  BoltIcon,
  EyeIcon,
  ArrowDownTrayIcon, // Added for download buttons
} from "@heroicons/react/24/outline";
import Header from "../../components/Layout/Header";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import globalStyle from "../../styles/globalStyle";

import logo from "../../assets/logo.png";
import botImage1 from "../../assets/bot-1.png";
import botImage2 from "../../assets/bot-2.png";
import botImage3 from "../../assets/bot-3.png";
import File1 from "../../assets/Certificate of Organization.pdf";
import File2 from "../../assets/Notice of Approval of Initial Filing.pdf";

const LoadingScreen: React.FC = () => (
  <div className="w-full h-screen bg-black">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <div className="text-center">
        <p className="mt-4 text-gray-400">Loading...</p>
      </div>
    </div>
  </div>
);

const HomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "home" | "about" | "terms" | "privacy" | "compliance"
  >("home");
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [currentBotImageIndex, setCurrentBotImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [imageOpacity, setImageOpacity] = useState(1);
  const siteName = process.env.REACT_APP_SITE_NAME || "RippleForge Analytics";

  const botImages = [botImage1, botImage2, botImage3];

  // UPDATED: Enhanced features with bot-specific capabilities
  const features = [
    {
      icon: <CpuChipIcon className="w-6 h-6" />,
      title: "AI-Driven Decision Making",
      description:
        "Machine learning models adapt to changing market conditions",
      color: "text-blue-500",
      bgColor: "bg-blue-900",
    },
    {
      icon: <BoltIcon className="w-6 h-6" />,
      title: "Automated Trade Execution",
      description: "Instant execution without delays or emotional interference",
      color: "text-green-500",
      bgColor: "bg-green-900",
    },
    {
      icon: <ChartBarIcon className="w-6 h-6" />,
      title: "Real-Time Market Analysis",
      description: "Continuous monitoring of price action, volume, and trends",
      color: "text-purple-500",
      bgColor: "bg-purple-900",
    },
    {
      icon: <ClockIcon className="w-6 h-6" />,
      title: "24/7 Market Monitoring",
      description: "Operates around the clock, even when you're offline",
      color: "text-yellow-500",
      bgColor: "bg-yellow-900",
    },
    {
      icon: <ShieldCheckIcon className="w-6 h-6" />,
      title: "Risk Management Controls",
      description:
        "Built-in safeguards to manage drawdowns and protect capital",
      color: "text-red-500",
      bgColor: "bg-red-900",
    },
    {
      icon: <EyeIcon className="w-6 h-6" />,
      title: "Performance Transparency",
      description: "Clear tracking of trades and performance metrics",
      color: "text-indigo-500",
      bgColor: "bg-indigo-900",
    },
  ];

  // UPDATED: How the bot works section
  const howItWorks = [
    {
      step: "1",
      title: "Connect Your Exchange",
      description: "Securely connect via API (you control your funds)",
      icon: <QrCodeIcon className="w-5 h-5" />,
    },
    {
      step: "2",
      title: "Configure Trading Strategy",
      description: "Set up AI parameters, stop-loss, and take-profit levels",
      icon: <CpuChipIcon className="w-5 h-5" />,
    },
    {
      step: "3",
      title: "AI Market Analysis",
      description: "Bot analyzes real-time data using machine learning models",
      icon: <ChartBarIcon className="w-5 h-5" />,
    },
    {
      step: "4",
      title: "Automated Execution",
      description: "Trades executed based on data-driven signals",
      icon: <BoltIcon className="w-5 h-5" />,
    },
  ];

  // Bot Capabilities - NEW section based on provided description
  const botCapabilities = [
    {
      title: "Real-Time Market Analysis",
      items: [
        "Price action and volume analysis",
        "Market trends and momentum indicators",
        "Volatility pattern recognition",
        "Historical data processing",
      ],
    },
    {
      title: "AI & Machine Learning",
      items: [
        "Advanced algorithm processing",
        "Signal confirmation models",
        "Adaptive learning from market data",
        "Pattern recognition across conditions",
      ],
    },
    {
      title: "Risk & Trade Management",
      items: [
        "Predefined risk management rules",
        "Stop-loss and take-profit mechanisms",
        "Disciplined trade execution",
        "Capital protection protocols",
      ],
    },
    {
      title: "XRP Ecosystem Focus",
      items: [
        "Optimized for XRP liquidity patterns",
        "Specialized volatility analysis",
        "Market behavior adaptation",
        "Ecosystem-specific strategies",
      ],
    },
  ];

  const tabs = [
    {
      id: "home",
      label: "Home",
      icon: <HomeIcon className="w-4 h-4" />,
    },
    {
      id: "about",
      label: "About",
      icon: <InformationCircleIcon className="w-4 h-4" />,
    },
    {
      id: "compliance",
      label: "Compliance",
      icon: <ShieldCheckIcon className="w-4 h-4" />,
    },
    {
      id: "terms",
      label: "Terms",
      icon: <DocumentTextIcon className="w-4 h-4" />,
    },
    {
      id: "privacy",
      label: "Privacy",
      icon: <LockClosedIcon className="w-4 h-4" />,
    },
  ];

  // Bot image rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setImageOpacity(0);
      setTimeout(() => {
        setCurrentBotImageIndex((prev) => (prev + 1) % botImages.length);
        setImageOpacity(1);
      }, 500);
    }, 6000);

    return () => clearInterval(interval);
  }, [botImages.length]);

  // Check if we need scroll buttons on mobile
  useEffect(() => {
    const checkScroll = () => {
      if (window.innerWidth < 768) {
        setShowScrollButtons(true);
      } else {
        setShowScrollButtons(false);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  // Scroll to top when tab changes
  useEffect(() => {
    const scrollToTop = () => {
      setIsLoading(true);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    };

    scrollToTop();
  }, [activeTab]);

  const scrollTabs = (direction: "left" | "right") => {
    const tabsContainer = document.querySelector(".tabs-container");
    if (tabsContainer) {
      const scrollAmount = 200;
      tabsContainer.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleTabChange = (
    tabId: "home" | "about" | "terms" | "privacy" | "compliance",
  ) => {
    setActiveTab(tabId);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className={globalStyle.container}>
      <Header />

      {/* Fixed Tabs Navigation */}
      <div className="sticky top-[4.5rem] sm:top-[5.5rem] z-30 bg-black border-b border-gray-800 shadow-sm">
        <div className="relative px-4 md:px-6 lg:px-8">
          {showScrollButtons && (
            <>
              <button
                onClick={() => scrollTabs("left")}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-black border border-gray-700 rounded-full p-1 shadow-sm"
              >
                <ChevronLeftIcon className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => scrollTabs("right")}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-black border border-gray-700 rounded-full p-1 shadow-sm"
              >
                <ChevronRightIcon className="w-5 h-5 text-gray-400" />
              </button>
            </>
          )}

          <div className="tabs-container flex items-center justify-start md:justify-center space-x-2 py-3 px-5 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <React.Fragment key={tab.id}>
                <Button
                  variant={activeTab === tab.id ? "primary" : "tertiary"}
                  size="sm"
                  onClick={() => {
                    handleTabChange(tab.id as any);
                  }}
                  className={`
                    flex items-center justify-center min-w-[120px] px-4 py-2.5 rounded-lg
                    whitespace-nowrap flex-shrink-0
                    ${activeTab === tab.id ? "shadow-md" : ""}
                  `}
                  leftIcon={tab.icon}
                >
                  {tab.label}
                </Button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <main className="page-content">
        {/* Hero Section - UPDATED with bot description */}
        {activeTab === "home" && (
          <div className="mb-12 md:mb-16 animate-fade-in">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12">
              {/* Left Content */}
              <div className="flex-1">
                <div className="mb-6">
                  <h1 className={globalStyle.h1}>AI-Powered XRP Trading Bot</h1>
                  <p className={`${globalStyle.body1} mt-4 text-gray-400`}>
                    Our AI-driven XRP trading bot analyzes market data in real
                    time and executes trades with precision, speed, and
                    discipline. Built specifically around the XRP ecosystem, the
                    system leverages advanced algorithms and machine learning
                    models to identify high-probability trading opportunities.
                  </p>
                </div>

                <div className="mb-8">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className={globalStyle.body2 + " text-gray-300"}>
                        AI-Driven Market Analysis
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className={globalStyle.body2 + " text-gray-300"}>
                        Automated Trade Execution
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className={globalStyle.body2 + " text-gray-300"}>
                        XRP-Focused Strategy
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className={globalStyle.body2 + " text-gray-300"}>
                        Risk Management Controls
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/register">
                    <Button size="lg" className="w-full sm:w-auto">
                      Signup
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      login
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Right Image with Fade Effect */}
              <div className="flex-1">
                <Card className="relative overflow-hidden border-0 shadow-lg">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <img src={logo} alt={siteName} className="w-10 h-10" />
                        <div>
                          <h3 className="font-semibold text-white">
                            {siteName}
                          </h3>
                          <p className="text-sm text-gray-400">
                            AI Trading Platform
                          </p>
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-primary/20 rounded-full">
                        <span className="text-xs text-primary font-medium">
                          BETA
                        </span>
                      </div>
                    </div>

                    {/* Bot Image with Fade Transition */}
                    <div className="relative w-full aspect-square rounded-lg overflow-hidden">
                      <img
                        src={botImages[currentBotImageIndex]}
                        alt="AI Trading Bot Interface"
                        className="w-full h-full object-cover transition-opacity duration-500"
                        style={{ opacity: imageOpacity }}
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-gray-800 rounded-lg">
                        <div className="text-lg font-semibold text-primary">
                          AI-Powered
                        </div>
                        <div className="text-xs text-gray-400">
                          Machine Learning
                        </div>
                      </div>
                      <div className="text-center p-3 bg-gray-800 rounded-lg">
                        <div className="text-lg font-semibold text-primary">
                          24/7 Trading
                        </div>
                        <div className="text-xs text-gray-400">
                          Automated Execution
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Bot Capabilities Section - NEW */}
        {activeTab === "home" && (
          <div className="mb-12 md:mb-16">
            <div className="text-center mb-8">
              <h2 className={globalStyle.h2}>How The AI Trading Bot Works</h2>
              <p
                className={`${globalStyle.body2} mt-2 text-gray-400 max-w-2xl mx-auto`}
              >
                The bot continuously monitors the XRP market using advanced
                algorithms to identify trading opportunities with discipline and
                precision
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.map((step, index) => (
                <div
                  key={index}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Card padding="lg" className="h-full">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary-900 text-primary flex items-center justify-center font-bold text-lg mr-3">
                        {step.step}
                      </div>
                      <div
                        className={`${step.icon.props.className} text-primary`}
                      >
                        {step.icon}
                      </div>
                    </div>
                    <h3 className={`${globalStyle.h6} mb-2`}>{step.title}</h3>
                    <p className={`${globalStyle.caption} text-gray-400`}>
                      {step.description}
                    </p>
                  </Card>
                </div>
              ))}
            </div>

            {/* Bot Capabilities Detail */}
            <div className="mt-12">
              <Card className="bg-gradient-to-br from-gray-900 to-black border border-gray-800">
                <div className="p-8">
                  <h3 className={`${globalStyle.h5} mb-6 text-center`}>
                    Advanced Bot Capabilities
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {botCapabilities.map((capability, index) => (
                      <div key={index} className="space-y-4">
                        <h4 className="font-semibold text-white">
                          {capability.title}
                        </h4>
                        <ul className="space-y-2">
                          {capability.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></div>
                              <span className="text-sm text-gray-400">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-800">
                    <p className="text-sm text-gray-400 text-center">
                      Unlike emotional or manual trading, our AI bot operates
                      based on data, logic, and predefined risk management
                      rules—helping to remove human error and inconsistency from
                      the trading process.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Main Content Cards */}
        <div>
          {/* About Us Tab Content - UPDATED with bot details */}
          {activeTab === "about" && (
            <Card padding="lg" className="animate-fade-in">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <InformationCircleIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className={globalStyle.h2}>About {siteName}</h2>
                    <div className="h-0.5 w-16 bg-primary rounded-full mt-2 mb-4"></div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-gray-800 rounded-xl">
                    <h3 className={`${globalStyle.h5} mb-4 flex items-center`}>
                      <CpuChipIcon className="w-5 h-5 text-primary mr-2" />
                      Our AI Trading Technology
                    </h3>
                    <p className={`${globalStyle.body2} text-gray-300 mb-4`}>
                      {siteName} provides an AI-driven XRP trading bot designed
                      to analyze market data in real time and execute trades
                      with precision, speed, and discipline. Our system
                      leverages:
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <li className="flex items-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">
                          Advanced algorithms
                        </span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">
                          Machine learning models
                        </span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">
                          Technical analysis
                        </span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">
                          Real-time market monitoring
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h3 className={`${globalStyle.h5} flex items-center`}>
                        <ChartBarIcon className="w-5 h-5 text-green-500 mr-2" />
                        Market Analysis
                      </h3>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        Our bot continuously monitors the XRP market using
                        real-time price action, volume analysis, market trends,
                        momentum indicators, volatility patterns, and AI-based
                        signal confirmation models.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h3 className={`${globalStyle.h5} flex items-center`}>
                        <ShieldCheckIcon className="w-5 h-5 text-green-500 mr-2" />
                        Risk Management
                      </h3>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        Built specifically around the XRP ecosystem, the system
                        includes predefined risk management rules, stop-loss
                        mechanisms, and take-profit strategies to help manage
                        drawdowns and protect capital.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-6">
                    <h3 className={`${globalStyle.h5} mb-4`}>
                      Core Principles
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-900 rounded-lg">
                        <div className="font-semibold text-primary mb-2">
                          Data-Driven
                        </div>
                        <p className="text-sm text-gray-400">
                          Removes emotional trading through algorithmic analysis
                        </p>
                      </div>
                      <div className="p-4 bg-gray-900 rounded-lg">
                        <div className="font-semibold text-primary mb-2">
                          User Control
                        </div>
                        <p className="text-sm text-gray-400">
                          Full API-based integration, you control all funds
                        </p>
                      </div>
                      <div className="p-4 bg-gray-900 rounded-lg">
                        <div className="font-semibold text-primary mb-2">
                          Transparency
                        </div>
                        <p className="text-sm text-gray-400">
                          Clear performance tracking and risk disclosures
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Compliance Tab Content - WITH NEW DOCUMENT DOWNLOAD SECTION */}
          {activeTab === "compliance" && (
            <Card padding="lg" className="animate-fade-in">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <ShieldCheckIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className={globalStyle.h2}>
                      Compliance & Risk Disclosure
                    </h2>
                    <div className="h-0.5 w-16 bg-primary rounded-full mt-2 mb-4"></div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* NEW: Company Formation Documents Section */}
                  <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-xl">
                    <h3 className={`${globalStyle.h5} mb-4 flex items-center`}>
                      <DocumentTextIcon className="w-5 h-5 text-primary mr-2" />
                      Company Formation Documents
                    </h3>
                    <p className="text-gray-400 mb-4">
                      Official company registration and approval documents for
                      verification and compliance purposes.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <a
                        href={File1}
                        download="Certificate of Organization.pdf"
                        className="flex items-center p-4 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors border border-gray-700 group"
                      >
                        <DocumentTextIcon className="w-8 h-8 text-primary mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-white group-hover:text-primary transition-colors">
                            Certificate of Organization
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            PDF Document • Click to download
                          </div>
                        </div>
                        <ArrowDownTrayIcon className="w-5 h-5 text-gray-500 group-hover:text-primary transition-colors" />
                      </a>

                      <a
                        href={File2}
                        download="Notice of Approval of Initial Filing.pdf"
                        className="flex items-center p-4 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors border border-gray-700 group"
                      >
                        <DocumentTextIcon className="w-8 h-8 text-primary mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-white group-hover:text-primary transition-colors">
                            Notice of Approval
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            PDF Document • Click to download
                          </div>
                        </div>
                        <ArrowDownTrayIcon className="w-5 h-5 text-gray-500 group-hover:text-primary transition-colors" />
                      </a>
                    </div>
                    <p className="text-xs text-gray-600 mt-4">
                      These documents verify the legal formation and regulatory
                      approval status of {siteName}.
                    </p>
                  </div>

                  {/* <div className="p-6 bg-red-900/20 border border-red-700/30 rounded-xl">
                    <h3 className={`${globalStyle.h5} mb-4 text-red-300`}>
                      <ExclamationTriangleIcon className="w-5 h-5 inline mr-2" />
                      Critical Risk Disclosure
                    </h3>
                    <div className="space-y-4">
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        <strong>
                          Cryptocurrency trading involves substantial risk of
                          loss.
                        </strong>
                        Our AI trading bot is a tool designed to assist with
                        trading decisions, not a guarantee of profit. Market
                        conditions can change rapidly.
                      </p>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        <strong>
                          Past performance does not guarantee future results.
                        </strong>
                        Users are encouraged to understand the risks involved
                        and trade responsibly with capital they can afford to
                        lose.
                      </p>
                    </div>
                  </div> */}

                  {[
                    {
                      title: "AI Bot Limitations",
                      content:
                        "Our AI-driven trading bot analyzes market data and executes trades based on algorithms and machine learning models. However, no AI system can predict market movements with 100% accuracy. The bot operates based on historical data and real-time analysis, but cryptocurrency markets are inherently unpredictable.",
                    },
                    {
                      title: "Funds & Custody",
                      content:
                        "All client funds remain fully controlled by clients through exchange API connections. We never take custody of your assets. You maintain full control and can disconnect the bot at any time.",
                    },
                    {
                      title: "No Investment Advice",
                      content:
                        "This platform provides automated trading tools only. We are not registered investment advisors. All trading decisions facilitated by the bot are executed based on your configured parameters, not our recommendations.",
                    },
                    {
                      title: "Performance & Backtesting",
                      content:
                        "Historical performance and backtest results are for informational purposes only. They do not guarantee future performance. Real market conditions may differ significantly from backtest scenarios.",
                    },
                  ].map((section, index) => (
                    <div
                      key={index}
                      className="pb-6 border-b border-gray-800 last:border-0"
                    >
                      <h3 className={`${globalStyle.h6} mb-2`}>
                        {section.title}
                      </h3>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Terms & Conditions Tab Content */}
          {activeTab === "terms" && (
            <Card padding="lg" className="animate-fade-in">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <DocumentTextIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className={globalStyle.h2}>Terms of Service</h2>
                    <div className="h-0.5 w-16 bg-primary rounded-full mt-2 mb-4"></div>
                    <p className={`${globalStyle.caption} text-gray-500`}>
                      Effective:{" "}
                      {new Date().toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                {/* <div className="p-4 bg-red-900/20 border border-red-700/30 rounded-lg">
                  <div className="flex">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200">
                      <strong>Important:</strong> By using our AI trading bot,
                      you acknowledge that cryptocurrency trading carries
                      extreme risk. You may lose all of your invested capital.
                      The bot provides analytical tools only.
                    </p>
                  </div>
                </div> */}

                <div className="space-y-6">
                  {[
                    {
                      title: "1. AI Trading Bot Service",
                      content:
                        "This platform provides an AI-driven XRP trading bot that analyzes market data in real time and executes trades based on algorithms and machine learning models. The service includes automated trade execution, risk management controls, and 24/7 market monitoring.",
                    },
                    {
                      title: "2. User Responsibilities & Risk Acceptance",
                      content:
                        "You accept full responsibility for all trading activities conducted through the bot. You acknowledge that cryptocurrency trading involves substantial risk of loss. You represent that you have sufficient knowledge and experience to understand these risks.",
                    },
                    {
                      title: "3. API Integration & Fund Security",
                      content:
                        "You are solely responsible for securing your exchange API keys and credentials. We are not liable for any unauthorized access to your exchange accounts. You must implement appropriate security measures including API key restrictions and two-factor authentication.",
                    },
                    {
                      title: "4. Bot Performance & Limitations",
                      content:
                        "The AI bot's performance depends on market conditions, algorithm effectiveness, and your configuration settings. We do not guarantee specific trading results. System outages, market volatility, or technical issues may affect performance.",
                    },
                    {
                      title: "5. XRP Market Specifics",
                      content:
                        "Our bot is optimized for XRP market behavior, liquidity patterns, and volatility characteristics. However, XRP trading carries unique risks including regulatory developments and ecosystem changes that may affect trading outcomes.",
                    },
                    {
                      title: "6. Subscription & Fees",
                      content:
                        "Access to the AI trading bot may require a subscription fee. Fees are clearly disclosed before purchase. Refund policies are outlined separately. We reserve the right to modify pricing with notice to existing users.",
                    },
                  ].map((section, index) => (
                    <div
                      key={index}
                      className="pb-6 border-b border-gray-800 last:border-0"
                    >
                      <h3 className={`${globalStyle.h6} mb-2`}>
                        {section.title}
                      </h3>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Privacy Policy Tab Content */}
          {activeTab === "privacy" && (
            <Card padding="lg" className="animate-fade-in">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <LockClosedIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className={globalStyle.h2}>Privacy Policy</h2>
                    <div className="h-0.5 w-16 bg-primary rounded-full mt-2 mb-4"></div>
                    <p className={`${globalStyle.caption} text-gray-500`}>
                      Last Updated:{" "}
                      {new Date().toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {[
                    {
                      title: "AI Bot Data Collection",
                      content:
                        "For the AI trading bot to function, we collect: (1) Trading data from your connected exchanges (position sizes, trade history, market data); (2) Bot performance metrics; (3) Strategy configuration settings; (4) Market analysis data processed by our algorithms. We do NOT collect or store your exchange API secrets or private keys.",
                    },
                    {
                      title: "How Trading Data Is Used",
                      content:
                        "Your trading data is used exclusively to: (1) Power the AI algorithms and machine learning models; (2) Execute trades according to your configured strategies; (3) Generate performance reports; (4) Improve our trading algorithms through anonymized, aggregated data analysis. We never share individual trading data with third parties.",
                    },
                    {
                      title: "Algorithm Training & Data Security",
                      content:
                        "Our AI models may be trained on anonymized, aggregated trading data to improve performance. All personal identifiers are removed before such training. We implement military-grade encryption for data in transit and at rest, with regular security audits and penetration testing.",
                    },
                    {
                      title: "Third-Party Data Processors",
                      content:
                        "We may use specialized third-party services for: (1) Market data feeds; (2) Cloud infrastructure; (3) AI model training; (4) Technical analytics. All processors are bound by strict confidentiality agreements and GDPR/CCPA compliance where applicable.",
                    },
                    {
                      title: "Data Retention for Bot Functionality",
                      content:
                        "We retain trading data necessary for bot functionality, performance tracking, and regulatory compliance. You can request deletion of your data, but this may disable certain bot features. Backups are maintained for disaster recovery purposes.",
                    },
                    {
                      title: "International Data Transfers",
                      content:
                        "As a global trading platform, data may be processed in various jurisdictions. We ensure all data transfers comply with international data protection standards through Standard Contractual Clauses or equivalent mechanisms.",
                    },
                  ].map((section, index) => (
                    <div
                      key={index}
                      className="pb-6 border-b border-gray-800 last:border-0"
                    >
                      <h3 className={`${globalStyle.h6} mb-2`}>
                        {section.title}
                      </h3>
                      <p className={`${globalStyle.body2} text-gray-300`}>
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </main>

      {/* Footer - Updated with bot focus */}
      <footer className="bg-black border-t border-gray-800 py-8">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <img src={logo} alt={siteName} className="w-8 h-8" />
                <div>
                  <span className="font-semibold text-white block">
                    {siteName}
                  </span>
                  <span className="text-xs text-gray-500">
                    AI XRP Trading Platform
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CpuChipIcon className="w-4 h-4 text-primary" />
                <span className="text-sm text-gray-400">
                  Technology meets discipline. Trade smarter with AI.
                </span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-800">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-sm text-gray-500 mb-4 md:mb-0">
                  © {new Date().getFullYear()} {siteName}. All rights reserved.
                </p>
                <p className="text-xs text-gray-600 text-center md:text-right max-w-md">
                  {siteName} provides AI-driven trading tools only. We are not
                  registered as a broker-dealer or investment advisor.
                  <br />
                  Cryptocurrency trading involves substantial risk. Past
                  performance does not guarantee future results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .animate-fade-in {
          animation: fadeIn 2s ease-in;
        }
        .animate-fade-in-up {
          animation: fadeInUp 2s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
